// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { TableRow, TableCell, Typography, Box, Chip, Tooltip, Autocomplete, TextField } from "@mui/material";
import type { HeaderMapping } from "../../utils/fieldMappingUtils";
import { SchemaFieldDefinition } from "@/constants/schema-fields/schemaFields";
import { useMemo } from "react";
import { TargetSchema } from "@/types";
import { SCHEMA_LABELS } from "../../utils/reviewUtils";
import { CriticalFieldEntry } from "../document-review/criticalFields";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

export interface CrossSchemaMappingInfo {
  schema: TargetSchema;
  fields: string[];
}

interface MappingRowProps {
  mapping: HeaderMapping;
  currentMapping: string[];
  availableFields: SchemaFieldDefinition[];
  /** Critical-field entries for the current schema. Empty for non-mappable schemas. */
  criticalFields: CriticalFieldEntry[];
  /**
   * Other schemas where this same detected header has mappings. Surfaces a
   * "also mapped in {Schema}" chip on the row so reviewers see at a glance
   * that the header is doing work on other tabs and don't need to wonder
   * whether to remap it here.
   */
  crossSchemaMappings?: CrossSchemaMappingInfo[];
  onMappingChange: (header: string, targetFields: string[]) => void;
  isDuplicate?: boolean;
}

/**
 * Individual row in the mapping table with field selection and status.
 *
 * Each option in the dropdown is annotated with a "Required" or "Recommended"
 * chip when the field is critical. Severity color encodes the tier (error =
 * required, warning = recommended); the tooltip carries the impact text.
 */
export const MappingRow = ({
  mapping,
  currentMapping,
  availableFields,
  criticalFields,
  crossSchemaMappings = [],
  onMappingChange,
  isDuplicate = false,
}: MappingRowProps) => {
  const isMapped = currentMapping.length > 0;
  const isMappedElsewhere = crossSchemaMappings.length > 0;

  const isUnknownField = useMemo(
    () => currentMapping.some((field) => !availableFields.find((f) => f.field === field)),
    [currentMapping, availableFields]
  );

  // Build the value array: include synthetic entries for unknown fields
  const selectedValues = useMemo(() => {
    return currentMapping.map((field) => {
      const existing = availableFields.find((f) => f.field === field);
      if (existing) return existing;
      // Synthetic entry for unknown fields
      return { field, label: field, description: "Unknown field", required: false } as SchemaFieldDefinition;
    });
  }, [currentMapping, availableFields]);

  const hasGroups = useMemo(() => availableFields.some((f) => f.group && f.group !== ""), [availableFields]);

  // Lookup map: field name → critical entry. Used by renderOption to attach
  // the severity chip + tooltip.
  const criticalByField = useMemo(() => {
    const map = new Map<string, CriticalFieldEntry>();
    for (const entry of criticalFields) map.set(entry.field, entry);
    return map;
  }, [criticalFields]);

  // Sort key for an option's criticality tier — critical fields surface to the
  // top of their group so reviewers see the load-bearing options first.
  // 0 = required, 1 = recommended, 2 = non-critical (default).
  const criticalityRank = (field: string): number => {
    const entry = criticalByField.get(field);
    if (!entry) return 2;
    return entry.severity === "required" ? 0 : 1;
  };

  // Combine available fields with any synthetic unknown entries for Autocomplete
  // options. Sort by group (so MUI's groupBy renders clean section headers —
  // grouped items must be contiguous), then by criticality tier (required →
  // recommended → non-critical), then alphabetically by label so reviewers can
  // scan within a tier.
  const options = useMemo(() => {
    const unknownFields = selectedValues.filter((v) => !availableFields.find((f) => f.field === v.field));
    const sorted = [...availableFields].sort((a, b) => {
      if (hasGroups) {
        const groupCompare = (a.group || "").localeCompare(b.group || "");
        if (groupCompare !== 0) return groupCompare;
      }
      const rankCompare = criticalityRank(a.field) - criticalityRank(b.field);
      if (rankCompare !== 0) return rankCompare;
      return a.label.localeCompare(b.label);
    });
    return [...sorted, ...unknownFields];
  }, [availableFields, selectedValues, hasGroups, criticalByField]);

  // Row chrome:
  //  - duplicate (a real error): subtle red tint, loud enough to alert
  //  - unmapped here AND not mapped elsewhere: subtle orange tint — the eye
  //    differentiates "this still needs attention" from rows already handled
  //  - mapped only elsewhere: muted opacity, pushes the eye past it
  //  - mapped here: no chrome (the default, working state)
  const needsAttention = !isMapped && !isMappedElsewhere;
  return (
    <TableRow
      sx={{
        backgroundColor: isDuplicate
          ? "rgba(211, 47, 47, 0.06)"
          : needsAttention
            ? "rgba(255, 152, 0, 0.07)"
            : "inherit",
        opacity: !isMapped && isMappedElsewhere ? 0.7 : 1,
        "& td": { verticalAlign: "middle", py: 1 },
      }}
    >
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {mapping.detectedHeader}
        </Typography>
        {crossSchemaMappings.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
            {crossSchemaMappings.map((info) => (
              <Tooltip
                key={info.schema}
                arrow
                placement="top-start"
                title={info.fields.map((f) => formatToTitleCase(f)).join(", ")}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Mapped in ${SCHEMA_LABELS[info.schema]}`}
                  sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    cursor: "help",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        )}
      </TableCell>

      <TableCell
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {String(mapping.sampleValue ?? "—")}
      </TableCell>

      <TableCell>
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={selectedValues}
          onChange={(_event, newValue) => {
            onMappingChange(
              mapping.detectedHeader,
              newValue.map((v) => v.field)
            );
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.field === value.field}
          groupBy={hasGroups ? (option) => option.group || "" : undefined}
          limitTags={3}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={currentMapping.length === 0 ? "Select field(s)…" : ""}
              error={Boolean(isUnknownField || isDuplicate)}
            />
          )}
          renderOption={(props, option) => {
            const critical = criticalByField.get(option.field);
            return (
              <li {...props} key={option.field}>
                <Box display="flex" alignItems="center" gap={0.75} width="100%">
                  <Tooltip title={option.field} placement="left" arrow>
                    <Typography variant="body2" sx={{ cursor: "help" }}>
                      {option.label}
                    </Typography>
                  </Tooltip>
                  {critical && (
                    <Tooltip title={critical.impact} placement="right" arrow>
                      <Chip
                        label={critical.severity === "required" ? "Required" : "Recommended"}
                        size="small"
                        color={critical.severity === "required" ? "error" : "warning"}
                        variant="outlined"
                        sx={{
                          height: "16px",
                          fontSize: "0.65rem",
                          cursor: "help",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const isUnknown = !availableFields.find((f) => f.field === option.field);
              const critical = criticalByField.get(option.field);
              // Mapped chip styling by precedence:
              //  - unknown field → filled error  (real error state — alarm is correct)
              //  - critical (any tier) → outlined primary  (marker for "important",
              //    not error — error/warning colors here read as "broken" which
              //    is the wrong message for a field that's correctly mapped)
              //  - non-critical → filled default
              // Severity tier (required vs recommended) lives in the tooltip
              // and on the dropdown options, where the chip label names the tier
              // explicitly so the color is contextualized.
              const tagProps = getTagProps({ index });
              if (isUnknown) {
                return <Chip {...tagProps} key={option.field} label={option.label} size="small" color="error" />;
              }
              if (critical) {
                return (
                  <Tooltip
                    key={option.field}
                    arrow
                    placement="top"
                    title={
                      <Box>
                        <Box sx={{ fontWeight: 600, mb: 0.25 }}>
                          {critical.severity === "required" ? "Required" : "Recommended"} field
                        </Box>
                        <Box>{critical.impact}</Box>
                      </Box>
                    }
                  >
                    <Chip {...tagProps} label={option.label} size="small" color="primary" variant="outlined" />
                  </Tooltip>
                );
              }
              return <Chip {...tagProps} key={option.field} label={option.label} size="small" />;
            })
          }
        />
        {(isUnknownField || isDuplicate) && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
            {isUnknownField
              ? "One or more selected fields aren't valid for this schema."
              : "Duplicate mapping — another header is already mapped to this field."}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};
